// src/App.js

import React, { useEffect } from 'react';
import { AuthProvider } from './Layout/AuthContext';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './Layout/Layout';
import CssBaseline from '@mui/material/CssBaseline';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Suppress benign ResizeObserver errors - must be at module level to catch early
const suppressResizeObserverErrors = () => {
  // Suppress errors in development overlay
  window.addEventListener('error', e => {
    if (
      e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      e.message === 'ResizeObserver loop limit exceeded'
    ) {
      const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay');
      if (resizeObserverErrDiv) {
        resizeObserverErrDiv.style.display = 'none';
      }
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });

  // Also suppress in console
  const originalError = console.error;
  console.error = (...args) => {
    if (
      typeof args[0] === 'string' &&
      (args[0].includes('ResizeObserver loop') ||
       args[0].includes('ResizeObserver loop completed'))
    ) {
      return;
    }
    originalError.apply(console, args);
  };
};

// Call immediately
suppressResizeObserverErrors();

function App() {
  // Additional suppression in component lifecycle
  useEffect(() => {
    const resizeObserverErrHandler = (e) => {
      if (
        e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
        e.message === 'ResizeObserver loop limit exceeded'
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    window.addEventListener('error', resizeObserverErrHandler);
    return () => window.removeEventListener('error', resizeObserverErrHandler);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Layout />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;